/**
 * Het trage vangnet naast de live-stroom (sessie 72, recensie blok 1).
 *
 * Nieuwe berichten komen via de live-stroom (SSE). Maar de lijst verandert
 * ook zonder event: tijd die verstrijkt (door Nova na 2 min terug naar nieuw,
 * het live-venster van 15 min, wekkers, `beslis_uiterlijk`) en bronnen die
 * geen event sturen (nieuwe taken van JP, retour- en reviewsignalen). Daarom
 * verversen lijst en menutellers ook bij een open stroom, maar traag. Dit is
 * geen polling voor berichten; zaak en concept doen niet mee.
 *
 * Is de stroom dicht, dan elke minuut (zoals vóór sessie 72). Komt JP terug
 * op het scherm en is de lijst ouder dan 30 s, dan één keer stil verversen.
 */
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { werkplekApi } from "@/lib/werkplek-api";

import { useLive } from "./live";
import { v6Sleutels } from "./sleutels";

/**
 * Vangnet bij een open live-stroom: 5 minuten. Kort genoeg dat een wekker of
 * een verlopen live-venster binnen een koffiepauze zichtbaar wordt, lang
 * genoeg dat het niet de zware `haal_lijst` (circa 45 queries) elke minuut
 * naast de stroom laat draaien.
 */
export const VANGNET_MS = 5 * 60_000;
/** Zonder live-stroom: elke minuut, zoals vóór sessie 72. */
export const POLL_DICHT_MS = 60_000;
/** Terug op het scherm: alleen verversen als de lijst ouder is dan dit. */
export const TERUGKOMST_OUDER_DAN_MS = 30_000;
/** Twee bijtrek-redenen kort na elkaar (terugkomst en herverbinding) geven één herlading. */
const SAMEN_MS = 5_000;

/** Het interval voor lijst en menutellers. */
export function vangnetInterval(verbonden: boolean): number {
  return verbonden ? VANGNET_MS : POLL_DICHT_MS;
}

export function useMenutellers() {
  const { verbonden } = useLive();
  return useQuery({
    queryKey: v6Sleutels.menutellers,
    queryFn: werkplekApi.menutellers,
    staleTime: 30_000,
    refetchInterval: vangnetInterval(verbonden),
  });
}

type Groep = "lijst" | "zaken";
const laatst = new WeakMap<QueryClient, Partial<Record<Groep, number>>>();

/**
 * Stil bijtrekken: `lijst` = lijst en menutellers, `zaken` = de open zaak.
 * Een groep die net (binnen 5 s) bijgetrokken is, slaat over.
 *
 * Werkplek live (28-10): `altijd` slaat dat over. Voor het moment dat de
 * stroom (weer) open is: wat daarvóór is opgehaald, kan een bericht missen
 * dat tussen ophalen en verbinden binnenkwam.
 */
export function bijtrekken(
  qc: QueryClient,
  groepen: Groep[],
  { altijd = false }: { altijd?: boolean } = {},
): void {
  const nu = Date.now();
  const stand = laatst.get(qc) ?? {};
  laatst.set(qc, stand);
  for (const groep of groepen) {
    if (!altijd && nu - (stand[groep] ?? 0) < SAMEN_MS) continue;
    stand[groep] = nu;
    if (groep === "lijst") {
      void qc.invalidateQueries({ queryKey: v6Sleutels.lijst });
      void qc.invalidateQueries({ queryKey: v6Sleutels.menutellers });
    } else {
      void qc.invalidateQueries({ queryKey: v6Sleutels.zaken });
    }
  }
}

/** Het moment van de nieuwste lijstdata in de cache (0 als er niets is). */
function nieuwsteLijst(qc: QueryClient): number {
  let nieuwste = 0;
  for (const q of qc.getQueryCache().findAll({ queryKey: v6Sleutels.lijst })) {
    nieuwste = Math.max(nieuwste, q.state.dataUpdatedAt);
  }
  return nieuwste;
}

/** Terug op het scherm: één keer lijst en menutellers als de lijst ouder is dan 30 s. */
export function useVerversBijTerugkomst(): void {
  const qc = useQueryClient();
  useEffect(() => {
    const zichtbaar = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - nieuwsteLijst(qc) <= TERUGKOMST_OUDER_DAN_MS) return;
      bijtrekken(qc, ["lijst"]);
    };
    document.addEventListener("visibilitychange", zichtbaar);
    return () => document.removeEventListener("visibilitychange", zichtbaar);
  }, [qc]);
}

/**
 * Werkplek live (28-10): meekijken zonder verversen, ook na de slaapstand
 * van de iPhone. Voor het scherm Vandaag (lijst plus open zaak).
 *
 * - Na elke verbinding van de live-stroom, ook de eerste, één keer de open
 *   zaak en de lijst. De zaak werd vóór de stroom geladen en pollt niet
 *   zolang de stroom open is; wat daartussen binnenkwam, staat er anders
 *   pas bij het volgende event.
 * - Direct bij terugkomst (zichtbaar, of `pageshow` uit de bfcache) de open
 *   zaak, en de lijst als die ouder is dan 30 s. De stroom verbindt dan zelf
 *   opnieuw (live.tsx) en trekt daarna nog één keer bij.
 *
 * Hergebruikt de bestaande queries via `bijtrekken`; geen tweede stroom en
 * geen polling.
 */
export function useBijtrekkenBijLive(): void {
  const qc = useQueryClient();
  const { verbindingen } = useLive();
  const gezien = useRef(verbindingen);
  useEffect(() => {
    // Gemonteerd terwijl de stroom al open was: de queries laden net vers.
    if (verbindingen === gezien.current) return;
    gezien.current = verbindingen;
    if (verbindingen > 0) bijtrekken(qc, ["zaken", "lijst"], { altijd: true });
  }, [verbindingen, qc]);

  useEffect(() => {
    const terug = () => {
      const groepen: Groep[] = ["zaken"];
      if (Date.now() - nieuwsteLijst(qc) > TERUGKOMST_OUDER_DAN_MS) groepen.push("lijst");
      bijtrekken(qc, groepen);
    };
    const zichtbaar = () => {
      if (document.visibilityState === "visible") terug();
    };
    const paginaTerug = (event: PageTransitionEvent) => {
      if (event.persisted) terug();
    };
    document.addEventListener("visibilitychange", zichtbaar);
    window.addEventListener("pageshow", paginaTerug);
    return () => {
      document.removeEventListener("visibilitychange", zichtbaar);
      window.removeEventListener("pageshow", paginaTerug);
    };
  }, [qc]);
}
